'use client';

import { useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';
import customCss from '@/app/components/hotel-detail/css/customCss';

/**
 * React sensor hook that tracks state of a CSS media query.
 *
 * similar to https://github.com/streamich/react-use/blob/master/docs/useMedia.md
 *
 *
 * **Try to avoid using this and any logic based on screen sizes in general!
 * We do SSR and do not know about screen sizes on server side.**
 *
 * ## The problem
 * `useMedia` hook will not correctly work on the Server as the browser size is unknown there.
 * This will create different DOM output on client than on server, causing React rehydration
 * to rebuild the complete DOM tree (very slow, bad, don't ever do that, … ).
 *
 * ## This solution
 * Instead of defaulting to some estimate value on server side this hook will always return `undefined` in the first render call.
 * Then it will measure the window and if the media queries breakpoint is met it will result in a second render based on the "real"
 * breakpoint data returning `true`.
 * Note that the first return value will always be `falsey` but not `false`. This will allow for shortcuts such as
 * ```
 * const isDesktop = useMediaSSR('(min-width: 480px)')
 * return isDesktop ? <DesktopComponent /> : <MobileComponent />
 * ```
 * as well as more fine grained approaches such as
 * ```
 * const isDesktop = useMediaSSR('(min-width: 480px)')
 * return isDesktop
 *   ? <DesktopComponent />
 *   : (isDesktop == null)
 *   ? <IntermediateComponent />
 *   : <MobiletComponent />
 * ```
 *
 * ## Still somewhat TBDy
 * **aka. some more reasons to try to avoid this approach at all**
 * - **possible initial double render** When the targeted break point is met this will always result in a double render.
 * This will not only happen upon initial app- but upon every component-mount.
 * Make sure not to have heavy computations in children.
 * - **always mobile first visible** The user will always see the "mobile" version for a short time.
 * Make sure to not have heavy visual breaks caused by subceeding "larger" layout.
 * - **Mobile Last**: This might introduce weird behavior if not working mobile first, eg. by adding `max-height` as query.
 * As we consider this beeing bad practice anyhow I didn't put any extra efforts into this.
 *
 * ## We are in 2021 now. Can we improve this?
 * Hopefully! Some interesting discussions about this topic are currently happening at `auto-id` component from https://reach.tech/.
 * Check them out!
 *
 */
export function useMediaSSR(query: string) {
  const [state, setState] = useState<boolean>();

  useIsomorphicLayoutEffect(() => {
    let mounted = true;
    const mql = window.matchMedia(query);
    const onChange = () => {
      if (!mounted) {
        return;
      }
      setState(mql.matches);
    };

    mql.addListener(onChange);
    setState(mql.matches);

    return () => {
      mounted = false;
      mql.removeListener(onChange);
    };
  }, [query]);

  return state;
}

/**
 * Hook that returns true if screen resolutions matches at least the Tablet breakpoint.
 *
 * Convenience wrapper around `useMediaSSR` using breakpoints from the current `Theme`.
 *
 * **Note: please read all those issues and warning above!!!**
 */
export function useIsTabletOrGreater(defaultValue?: boolean) {
  const isTabletOrGreater = useMediaSSR(customCss.breakpoints.sm);
  return isTabletOrGreater == null ? defaultValue : isTabletOrGreater;
}

/**
 * Hook that returns true if screen resolutions matches at least the Desktop breakpoint.
 *
 * Convenience wrapper around `useMediaSSR` using breakpoints from the current `Theme`.
 *
 * **Note: please read all those issues and warning above!!!**
 */
export function useIsDesktopOrGreater(defaultValue?: boolean) {
  const isDesktopOrGreater = useMediaSSR(customCss.breakpoints.xl);
  return isDesktopOrGreater == null ? defaultValue : isDesktopOrGreater;
}

export const useIsMobile = (defaultValue?: boolean) => {
  const isTabletOrGreater = useIsTabletOrGreater();
  return isTabletOrGreater == null ? defaultValue : !isTabletOrGreater;
};

export function useIsLgOrGreater(defaultValue?: boolean) {
  const isLgOrGreater = useMediaSSR(customCss.breakpoints.lg);
  return isLgOrGreater == null ? defaultValue : isLgOrGreater;
}

export function useIsLgPlusOrGreater(defaultValue?: boolean) {
  const isLgOrGreater = useMediaSSR(customCss.breakpoints.lgPlus);
  return isLgOrGreater == null ? defaultValue : isLgOrGreater;
}
